// ANGEL FLIGHT — ALL gameplay + projection tunables (single source of truth).
//
// First-person gunner. The PROJECTION block defines how world space (lateral x,
// depth z, height y) maps to the screen — this is what makes the battlefield
// recede into the distance and zombies grow as they charge. No magic numbers
// elsewhere. Times in ms; distances in metres (world) or px (screen).

export const BALANCE = Object.freeze({
  VIEW: { width: 1280, height: 720 },

  /** Perspective projection (the FP depth illusion). */
  proj: {
    /** Focal length — bigger = flatter/longer lens. */
    focal: 520,
    /** Camera eye height above the ground plane (metres). */
    eyeHeight: 1.7,
    /** Horizon line as a fraction of screen height (where z→∞ converges). */
    horizonY: 0.46,
    /** Near plane (metres) — closest a zombie gets before it's "on" you. */
    nearZ: 1.2,
    /** Far plane (metres) — spawns appear here, fade in from the haze. Kept
     *  modest so zombies are READABLE at spawn and LOOM as they close. */
    farZ: 26,
    /** Lateral spread (metres) the spawn field covers each side of centre. */
    halfWidth: 9,
    /** World→screen lateral gain — also sets sprite px-per-metre (= gain / z).
     *  Bigger = enemies loom larger as they close. */
    lateralGain: 620,
    /** (reserved) reference sprite px-per-metre knob. */
    spritePxPerMetre: 150,
  },

  /** The gun (foreground hero) + aim. */
  gun: {
    /** Resting muzzle screen position (the gun pokes up from bottom-centre). */
    muzzleX: 640,
    muzzleY: 540,
    /** How far the reticle can swing from centre (px). */
    aimRangeX: 560,
    aimRangeY: 230,
    /** Exponential smoothing rate of the reticle toward the finger. */
    smoothLambda: 18,
    spinupMs: 180,
    tapMs: 130,
    recoilPx: 14,
    recoilReturnMs: 90,
    swayPx: 3, // idle breathing sway
  },

  /** Per-shot + impact juice (SHORT timings = punchy). */
  juice: {
    muzzleLifeMs: 70,
    tracerLifeMs: 70,
    shakePerShot: 0.05,
    shakeOnKill: 0.12,
    shakeOnHurt: 0.4,
    shakeMax: 1,
    shakeDecay: 1.7,
    shakeMaxOffset: 16,
    hitstopKillMs: 50,
    hitstopHeadshotMs: 90,
    hitFlashMs: 60,
    bloodLifeMs: 600,
  },

  /** The undead horde. */
  enemies: {
    /** The bird's forward traverse over the field (m/s) — the SIM half of the
     *  flight illusion. Every ground unit's closing rate gains this drift (the
     *  world slides past the helicopter) and RENDER.flight.scrollMps mirrors
     *  the SAME value for the ground/prop scroll. The invariant that kills the
     *  "they stand still / walk backward" read for good:
     *    closing = worldDriftMps + legSpeed,  groundScroll = worldDriftMps
     *  → motion relative to the terrain = legSpeed ≥ 0, ALWAYS. A parked RPG
     *  shooter stands his ground while the bird overtakes him; walkers visibly
     *  RUN forward over the asphalt; nobody ever moonwalks.
     *  1.25 (was 2.5 → 1.6): at 2.5 the bird overtook the field so fast the
     *  whole encounter read as enemies being thrown at the screen; the playtest
     *  on 1.6 STILL read as "they rush past before I can react", because the
     *  drift adds to every unit's closing rate. 1.25 keeps a visible forward
     *  traverse (the frame never statics) while buying each unit ~1s more of
     *  reactable time across the field. Per-enemy speed jitter + staggered
     *  spawns (below) carry the motion variety instead. */
    worldDriftMps: 1.25,
    /** Depth (metres) at which a reaching enemy strikes the gunner. */
    strikeZ: 1.4,
    maxLive: 60,
    /** Ms between individual spawns in a wave burst — the wave TRICKLES onto
     *  the field through the fog instead of materialising as one wall (the
     *  audited "they all teleport in stacked together" read). 300 (was 220):
     *  the longer trickle paces target acquisition — you fight a stream, not
     *  a wall. */
    spawnStaggerMs: 300,

    /** Depth pad (metres) BEYOND proj.farZ in which fresh spawns materialise.
     *  The whole band sits at/behind the haze reveal's hidden line
     *  (RENDER.spawnHaze.fadeHiddenZ = proj.farZ), so enemies are already on
     *  the field and WALK INTO the frame through the fog — they never pop into
     *  an open, fully-visible midfield. */
    spawnDepthPadM: 8,

    /** Behaviour tunables (per-type knobs live on EnemyDef; these are global). */
    behavior: {
      /** `flank`: how fast (metres/sec) a flanker slides toward its lateral
       *  edge while it closes. */
      flankDriftMps: 1.8,
      /** Ranged enemies at their standoff line strafe laterally at this speed
       *  (metres/sec) instead of statuing in place — they hold the LINE, not
       *  the spot, so the firing line stays alive and harder to hose. */
      standoffStrafeMps: 0.9,
      /** Lateral half-span (metres) of the standoff strafe weave. */
      standoffStrafeSpanM: 2.4,
      /** Probability a `march` grunt is instead tagged a FLANKER at spawn (it
       *  fans out to a lateral edge as it closes — so the filler horde reads as a
       *  pincer, not a column). Deterministic via ctx.rng. */
      marchFlankChance: 0.35,
      /** Per-enemy leg-speed jitter rolled at spawn (multiplier on def.speed,
       *  uniform in [min,max], ctx.rng). Same-type units no longer march in
       *  lockstep — stragglers fall behind, eager ones pull ahead, and a wave
       *  arrives as a loose mob instead of one stacked rank. */
      speedJitter: { min: 0.85, max: 1.3 },
      /** Cap on concurrent enemy projectiles in flight (pool ceiling). */
      maxProjectiles: 48,

      /** Wave spawn formations (G2): each firing wave picks ONE shape via
       *  ctx.rng, so reinforcements stop arriving as a straight row. `line`
       *  keeps the legacy random scatter; the rest arrange the wave. */
      formations: {
        /** Pick weights (normalised at use; ordering = tie-break order). */
        weights: { line: 0.25, wedge: 0.25, cluster: 0.25, pincer: 0.25 },
        /** Formation spawn depth base, as a fraction of farZ. 1.0 = the wave
         *  assembles AT the far plane and its shape staggers DEEPER into the
         *  haze — the manoeuvre emerges through the fog instead of popping
         *  into the visible midfield. */
        baseZFrac: 1.0,
        /** wedge: extra depth (m) at the outer edges — the V opens away. */
        wedgeDepth: 4,
        /** cluster: lateral blob radius (m) around the picked centre lane —
         *  loose enough that the blob never reads as one stacked clump. */
        clusterRadius: 3.6,
        /** Fraction of halfWidth the wave's centre lane may roll across —
         *  wider = waves assemble all over the field, not around the middle. */
        centerSpread: 0.75,
        /** cluster/pincer: per-rank depth stagger (m). */
        rankStagger: 1.6,
        /** pincer: fraction of halfWidth the two prongs hug. */
        pincerEdge: 0.85,
      },
    },
  },

  /** Player. */
  player: {
    baseHull: 100,
    invulnMs: 240,
    /** Quiet window (ms) the bird must go WITHOUT taking damage before Field
     *  Repair (the autoRepair upgrade) starts patching the hull. Long enough
     *  that regen is a between-contacts reward, never a mid-firefight crutch. */
    autoRepairDelayMs: 3500,
  },

  /** Countermeasure FLARES (key F). While the window is live, every flare-able
   *  rocket in flight veers off harmlessly (decoyed). Deliberately forgiving:
   *  the window comfortably covers a rocket's whole flight (~0.9s from a 12m
   *  standoff at 14 m/s), so "roughly timed" is good enough — the skill is in
   *  not wasting the cooldown. Bullets and acid globs are NOT flare-able. */
  flares: {
    /** How long (ms) one flare burst keeps decoying rockets. */
    activeMs: 1500,
    /** Cooldown (ms) between bursts. */
    cooldownMs: 6000,
  },

  /** Combo economy. */
  combo: {
    killsPerStep: 6,
    steps: [1, 1.5, 2, 3, 4] as const,
    decayMs: 2600,
  },

  /** Friendly fire (AC-130 "DON'T HIT FRIENDLIES"). Shooting a BLUEFOR friendly
   *  is a hard penalty: it breaks the combo, docks SCRIP, and counts a STRIKE;
   *  the Nth strike FAILS the mission. Strikes are debounced INCIDENTS, not
   *  rounds: every friendly round within `strikeWindowMs` of a counted strike
   *  merges into it, so one 150ms trigger-sweep across a trooper (2-3 rounds at
   *  17rps, or a whole flamer cone tick) is ONE strike — careless SUSTAINED fire
   *  still racks a strike per window and fails, but a single mistake doesn't
   *  end the mission. The penultimate strike fires `friendly:warning` (the
   *  CHECK FIRE klaxon) so the final strike is never a surprise. */
  friendlyFire: {
    /** SCRIP docked per friendly STRIKE (clamped so it never goes below zero). */
    scripPenalty: 50,
    /** Strike count that FAILS the mission (reason 'FRIENDLY FIRE'). */
    failThreshold: 3,
    /** Friendly rounds landing within this window of a counted strike merge
     *  into that strike (the window does NOT refresh on merged rounds, so a
     *  sustained hose still accrues one strike per window). */
    strikeWindowMs: 600,
  },

  /** The Armory's storefront (weapon unlocks + the salvage workshop). */
  armory: {
    /** SCRIP price per weapon unlock. The ladder paces the campaign: a solid M1
     *  clear (~250-400 scrip with upgrades pacing) buys the autocannon; the
     *  flamer lands mid-M2 grind; the railgun is the campaign's capstone buy.
     *  The starter minigun is free (always owned). */
    weaponPrices: {
      minigun: 0,
      autocannon: 250,
      flamer: 450,
      railgun: 800,
    },
    /** SALVAGE → workshop exchange rate: one salvage stands in for this much
     *  SCRIP when buying an upgrade tier (the "field workshop retrofit"). Keeps
     *  salvage spendable (the research ruling: salvage buys repairs/upgrade
     *  ticks) without a third currency: elites drop 2 salvage, so a brutal run
     *  (~10 elites) covers an early tier outright. */
    scripPerSalvage: 30,
    /** Floor on any salvage retrofit price (no free ticks at low tiers). */
    minSalvageCost: 3,
  },

  /** Pool prewarm sizes. */
  pools: {
    enemies: 60,
    tracers: 48,
    muzzles: 16,
    bloods: 40,
    blooddecals: 32,
    enemyProjectiles: 48,
  },
} as const);
